import { createContext, useContext, useMemo } from 'react';

import useBreadcrumb from '../hooks/useBreadcrumb';

const BreadcrumbStateContext = createContext(null);
const BreadcrumbDispatchContext = createContext(null);

/**
 * @param {Object} props
 * @param {BreadcrumbStep|BreadcrumbStep[]} props.steps - Initial breadcrumb steps
 * @param {number} [props.initialStep=0] - Initial active step index
 * @param {React.ReactNode} props.children
 */
const BreadcrumbProvider = ({ steps, initialStep = 0, children }) => {
  const breadcrumb = useBreadcrumb(steps, initialStep);

  const stateValue = useMemo(
    () => ({
      steps: breadcrumb.steps,
      step: breadcrumb.step,
      currentStep: breadcrumb.currentStep,
      current: breadcrumb.current,
      direction: breadcrumb.direction
    }),
    [
      breadcrumb.steps,
      breadcrumb.step,
      breadcrumb.currentStep,
      breadcrumb.current,
      breadcrumb.direction
    ]
  );

  const dispatchValue = useMemo(
    () => ({
      nextCrumb: breadcrumb.nextCrumb,
      goBack: breadcrumb.goBack,
      goNext: breadcrumb.goNext,
      goToStep: breadcrumb.goToStep,
      setStep: breadcrumb.setStep,
      setSteps: breadcrumb.setSteps,
      setStepWithTrim: breadcrumb.setStepWithTrim
    }),
    [
      breadcrumb.nextCrumb,
      breadcrumb.goBack,
      breadcrumb.goNext,
      breadcrumb.goToStep,
      breadcrumb.setStep,
      breadcrumb.setSteps,
      breadcrumb.setStepWithTrim
    ]
  );

  return (
    <BreadcrumbStateContext.Provider value={stateValue}>
      <BreadcrumbDispatchContext.Provider value={dispatchValue}>
        {children}
      </BreadcrumbDispatchContext.Provider>
    </BreadcrumbStateContext.Provider>
  );
};

/**
 * Access breadcrumb state (steps, step, currentStep, current, direction).
 * Only re-renders when state changes.
 * Must be used within a <BreadcrumbProvider>.
 */
const useBreadcrumbState = () => {
  const context = useContext(BreadcrumbStateContext);
  if (!context) {
    throw new Error('useBreadcrumbState must be used within a BreadcrumbProvider');
  }
  return context;
};

/**
 * Access breadcrumb navigation actions (nextCrumb, goBack, goNext, goToStep, setStep, setSteps, setStepWithTrim).
 * Stable references -- does not re-render on state changes.
 * Must be used within a <BreadcrumbProvider>.
 */
const useBreadcrumbDispatch = () => {
  const context = useContext(BreadcrumbDispatchContext);
  if (!context) {
    throw new Error('useBreadcrumbDispatch must be used within a BreadcrumbProvider');
  }
  return context;
};

/**
 * Access all breadcrumb state and navigation from context.
 * Convenience hook that combines state + dispatch (backward-compatible).
 * Must be used within a <BreadcrumbProvider>.
 *
 * @returns {import('../lib/propTypes').BreadcrumbReturn & import('../lib/propTypes').BreadcrumbItems}
 */
const useBreadcrumbContext = () => {
  const state = useBreadcrumbState();
  const dispatch = useBreadcrumbDispatch();
  return useMemo(() => ({ ...state, ...dispatch }), [state, dispatch]);
};

export {
  BreadcrumbStateContext,
  BreadcrumbDispatchContext,
  BreadcrumbProvider,
  useBreadcrumbState,
  useBreadcrumbDispatch,
  useBreadcrumbContext
};
